import { ISO_DATE_FORMAT } from '@fox/atoms/utils/date-functions';
import { strIsNullOrEmpty, strRemoveAllSpaces } from '@fox/atoms/utils/string';
import { validateEmail } from '@fox/atoms/utils/validation';
import { IWorkBenchLayout } from '@fox/blocks/contracts/work-bench';
import moment from 'moment';
import { IQuestionAnswer } from '../../../form-root/contracts';
import { FormRenderMode } from '../../../form-runtime-context/contracts';
import { ISection } from '../../section-block/contracts';
import { ConditionResult } from '../conditionals/contracts';
import { renderConditionalCheck } from '../conditionals/helpers';
import { addressRequirementsValidator } from '../validators/address-requirements-validator';
import { dataTableRequirementsValidator } from '../validators/data-table-requirements-validator';
import { subscriptionRequirementsValidator } from '../validators/subscription-requirements-validator';
import { ticketRequirementsValidator } from '../validators/ticket-requirements-validator';

export type QuestionValidator = (answer: IQuestionAnswer | undefined, metadataJson?: string, waitlistSubmit?: boolean) => boolean;
const QUESTION_VALIDATORS: Record<string, QuestionValidator> = {
	Subscription: subscriptionRequirementsValidator,
	ticket: ticketRequirementsValidator,
	Address: addressRequirementsValidator,
	DataTable: dataTableRequirementsValidator
};

export function haveRequiredFields(fields: ISection['fields']): boolean {
	for (const f of fields ?? []) {
		const meta = f.metadataJson ? JSON.parse(f.metadataJson) : {};

		if (meta.required) {
			return true;
		}
		// Handle special cases for 'CreatePerson' or 'CreatePeople'
		if (f.type === 'CreatePerson' || f.type === 'CreatePeople') {
			const requiredFields = [
				...Object.values(meta.editFields ?? {}).filter(value => value && value['meta']?.required === true),
				...Object.values(meta.businessFields ?? {}).filter(value => value && value['meta']?.required === true)
			];

			if (requiredFields.length > 0) {
				return true;
			}
		}
	}
	return false;
}

const validateContactAnswer = (requiredFields: [string, unknown][], values: Record<string, unknown>) => {
	if (!requiredFields || requiredFields.length === 0) return true;
	return requiredFields.every(f => {
		const fieldName = f[0];
		const answerValue = values[fieldName];
		if (Array.isArray(answerValue)) {
			return answerValue.length > 0;
		} else {
			if (fieldName.startsWith('Address:')) {
				const validAddress = addressRequirementsValidator({
					answer: { answerJson: answerValue }
				} as IQuestionAnswer);
				return validAddress;
			} else if (fieldName === 'email' && typeof answerValue === 'string') {
				return validateEmail(answerValue);
			} else if (typeof answerValue === 'string') {
				return !strIsNullOrEmpty(strRemoveAllSpaces(answerValue));
			} else return !!answerValue;
		}
	});
};

export const checkAllowNext = (input: {
	currentPage: ISection;
	formRenderMode: FormRenderMode;
	getAnswers: () => IQuestionAnswer[];
	layout: IWorkBenchLayout;
	sections: ISection[];
	waitlistSubmit?: boolean;
}) => {
	const { currentPage, formRenderMode, waitlistSubmit, getAnswers, layout, sections } = input;
	const answers = getAnswers();
	let allowNext = true;
	if (haveRequiredFields(currentPage?.fields)) {
		currentPage?.fields?.forEach(f => {
			var meta = f.metadataJson ? JSON.parse(f.metadataJson) : {};
			const answer = answers?.find(a => a.questionName === f.name);
			if (f.type === 'CreatePerson' || f.type === 'CreatePeople') {
				const requiredFields = [
					...Object.entries(meta.editFields ?? {}).filter(([key, value]) => value && value['meta']?.required === true),
					...Object.entries(meta.businessFields ?? {}).filter(([key, value]) => value && value['meta']?.required === true)
				];
				const conditionResult: ConditionResult = renderConditionalCheck(
					sections,
					meta.conditionalDefinitions,
					answers ?? [],
					formRenderMode,
					f.name,
					layout
				);
				if (conditionResult === ConditionResult.Invalid) {
					// ignore me
					return;
				}
				if (answer?.answer?.answerJson) {
					const parsedJson = JSON.parse(answer?.answer?.answerJson);
					//make sure the old forms still work
					if (parsedJson?.contacts) {
						parsedJson.contacts.forEach(contact => {
							const requiredForValidation = [...requiredFields];
							if (!strIsNullOrEmpty(contact['email']) && !requiredForValidation.find(r => r[0] === 'email')) {
								// ensure valid email if it is entered even if not required
								// would be handy if these fields had their own validators
								requiredForValidation.push(['email', meta.editFields['email']]);
							}
							if (!validateContactAnswer(requiredForValidation, contact)) {
								allowNext = false;
							}
						});
					} else {
						if (!strIsNullOrEmpty(parsedJson['email']) && !requiredFields.find(r => r[0] === 'email')) {
							requiredFields.push(['email', meta.editFields['email']]);
						}
						if (!validateContactAnswer(requiredFields, parsedJson)) {
							allowNext = false;
						}
					}
				} else if (requiredFields.length > 0) {
					allowNext = false;
				}
			} else {
				if (meta.required) {
					// Check if the question has conditionalDefinition if so check that condition against the matching answer
					// If the condition is not valid then this question is no longer required as the question is not visible
					const conditionResult: ConditionResult = renderConditionalCheck(
						sections,
						meta.conditionalDefinitions,
						answers ?? [],
						formRenderMode,
						f.name,
						layout
					);
					if (conditionResult === ConditionResult.Invalid) {
						// ignore me
					} else if (QUESTION_VALIDATORS[f.type]) {
						const validation = QUESTION_VALIDATORS[f.type](answer, f.metadataJson, waitlistSubmit);
						// We only want to change allowNext if validation fails
						if (!validation) allowNext = false;
					} else if (!answer) {
						// answer not yet filled in
						allowNext = false;
						return;
					} else if (answer?.answer?.answerString) {
						if (answer?.answer?.answerString === '__other' && strIsNullOrEmpty(answer?.answer?.answerOther)) {
							allowNext = false;
						}
					} else if (answer?.answer?.answerJson) {
						const parsedJson = JSON.parse(answer?.answer?.answerJson);
						if (Array.isArray(parsedJson) && parsedJson?.length > 0) {
							if (parsedJson.some(a => a === '__other') && strIsNullOrEmpty(answer?.answer?.answerOther)) {
								allowNext = false;
							}
						} else {
							const numberOfAnswers = Object.keys(parsedJson)?.length;
							const requiredAnswers = meta.questions?.length ?? meta.numberOfQuestions;
							if (!!requiredAnswers && requiredAnswers !== numberOfAnswers) {
								allowNext = false;
							}
						}
					} else if (answer?.answer?.answerInt !== undefined && answer?.answer?.answerInt !== null) {
						if (typeof answer?.answer?.answerInt !== 'number' || isNaN(answer?.answer?.answerInt)) {
							allowNext = false;
						}
					} else if (answer?.answer?.answerDate) {
						if (!moment(answer.answer.answerDate, ISO_DATE_FORMAT, true).isValid()) {
							allowNext = false;
						}
					} else {
						allowNext = false;
					}
				}
			}
		});
	}

	return allowNext;
};
